/**
 * @file useOrderElapsedTimer.ts
 * @description Hook calculating elapsed preparation time and color-coded delay tiers.
 */

import { useState, useEffect } from 'react';

export type DelayTier = 'normal' | 'warning' | 'delayed';

export interface ElapsedInfo {
  elapsedText: string;
  minutes: number;
  tier: DelayTier;
}

export const useOrderElapsedTimer = (createdAt: string, isDone: boolean): ElapsedInfo => {
  const [elapsed, setElapsed] = useState<ElapsedInfo>(() => calculateElapsed(createdAt));

  useEffect(() => {
    if (isDone) return;

    const interval = setInterval(() => {
      setElapsed(calculateElapsed(createdAt));
    }, 15000); // 15-second refresh interval

    return () => clearInterval(interval);
  }, [createdAt, isDone]);

  return elapsed;
};

const calculateElapsed = (createdAt: string): ElapsedInfo => {
  const start = new Date(createdAt).getTime();
  const now = Date.now();
  const diffMinutes = Math.max(0, Math.floor((now - start) / 60000));

  let tier: DelayTier = 'normal';
  if (diffMinutes >= 25) {
    tier = 'delayed';
  } else if (diffMinutes >= 15) {
    tier = 'warning';
  }

  return {
    elapsedText: `${diffMinutes}m`,
    minutes: diffMinutes,
    tier,
  };
};
